"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Building2, House as Home, Factory, ShieldCheck, CircleCheck as CheckCircle2, ArrowRight, MapPin, Clock } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const localCoverages = [{
  icon: Building2,
  title: "Town-centre commercial",
  description: "Pedestrian gantries, pavement licences, and shopfront access along High Street and town-centre retail zones."
}, {
  icon: Home,
  title: "Residential terraces & homes",
  description: "Safe access for re-roofing, chimney work, extensions, and solar installations across High Wycombe neighbourhoods."
}, {
  icon: Factory,
  title: "Industrial estates",
  description: "Heavy-duty loading bays, interior towers, and roof maintenance access for industrial parks including Cressex and Sands."
}];
const credentials = ["NASC accredited contractor", "CHAS safety certified", "Pete Wood's 20+ years trade experience", "Full public & employer liability cover"];
export default function Highwycombe() {
  const shouldReduceMotion = useReducedMotion();
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="highwycombeintro" data-nav="light" className="relative bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start">
          {/* Main Content Column */}
          <motion.div className="lg:col-span-7 flex flex-col justify-center" initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeIn}>
            <div className="flex items-center gap-2 mb-4">
              <Badge variant="outline" className="px-3 py-1 text-xs font-semibold gap-1.5 border-border bg-card">
                <MapPin className="size-3.5 text-primary" />
                <span>High Wycombe &amp; Buckinghamshire</span>
              </Badge>
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-6 font-default">
              Commercial and residential scaffolding in High Wycombe
            </h2>

            <div className="space-y-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
              <p className="font-medium text-foreground">
                BMF Scaffolding provides commercial, residential, roofing, solar and specialist scaffolding in High Wycombe and the surrounding Buckinghamshire area, including HP10 to HP15.
              </p>
              <p>
                Led by Director Pete Wood with over 20 years of hands-on scaffolding experience, our team delivers access solutions tailored to the town&apos;s specific architectural mix.
              </p>
              <p>
                From tight-access residential terraces and chimney repairs in local neighbourhoods to perimeter scaffolding for town-centre commercial properties and high-load platforms on nearby industrial estates, we handle every setup with structured oversight. With full NASC and CHAS accreditations, we ensure clear compliance, tidy handovers, and reliable timelines on every site.
              </p>
            </div>

            {/* Accreditation points */}
            <div className="mt-8 pt-6 border-t border-border">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                {credentials.map((item, i) => <div key={i} data-index={i} className="flex items-center gap-2.5">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span className="text-sm font-medium text-foreground">{item}</span>
                  </div>)}
              </div>
            </div>

            {/* Action buttons */}
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="shadow-sm">
                <Link to="/ContactRequestQuotePage">
                  Request a site quote
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/AreasWeCoverPage">View service areas</Link>
              </Button>
            </div>
          </motion.div>

          {/* Local Context Sidebar / Cards */}
          <motion.div className="lg:col-span-5 flex flex-col gap-4" initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeIn}>
            <div className="bg-muted/50 rounded-2xl p-6 sm:p-8 border border-border">
              <h3 className="text-lg font-bold text-foreground mb-1 font-default">
                Local scaffolding capability
              </h3>
              <p className="text-xs sm:text-sm text-muted-foreground mb-6">
                Engineered access suited to South Buckinghamshire terrain and property styles.
              </p>

              <div className="space-y-4">
                {localCoverages.map((item, i) => {
                const Icon = item.icon;
                return <Card key={i} data-index={i} className="border border-border/80 bg-card shadow-xs hover:border-primary/40 transition-[border-color,box-shadow] duration-200">
                      <CardContent className="p-4 sm:p-5 flex items-start gap-4">
                        <div className="p-2.5 rounded-lg bg-primary/10 text-primary shrink-0 mt-0.5">
                          <Icon className="size-5" />
                        </div>
                        <div className="space-y-1">
                          <h4 className="text-sm font-semibold text-foreground font-default">
                            {item.title}
                          </h4>
                          <p className="text-xs sm:text-sm text-muted-foreground leading-normal">
                            {item.description}
                          </p>
                        </div>
                      </CardContent>
                    </Card>;
              })}
              </div>

              <Separator className="my-6" />

              <div className="flex items-center justify-between gap-4 text-xs text-muted-foreground">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="size-4 text-primary" />
                  <span>NASC &amp; CHAS verified</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="size-4 text-primary" />
                  <span>20+ years experience</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}